import { useMemo } from 'react'

import {
  Area,
  AreaChart as RCAreaChart,
  Tooltip as RCTooltip,
  ResponsiveContainer,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'

import styles from './Chart.module.scss'
import { Tick, Tooltip } from './components'

interface RenderProps<T> {
  index: number
  payload: { value: T }
  x: number
  y: number
}

interface AreaChartProps<X> {
  data: { key: X; value: number }[]
  formatTooltipLabel: (value: string) => string
  formatTooltipValue?: (value: number, index: number) => JSX.Element | string
  formatXTick: (value: X) => string
  formatYTick: (value: number) => string
  reversed?: boolean
  yScaleFactor?: number
}

const AreaChart = <X,>({
  data,
  formatTooltipLabel,
  formatTooltipValue,
  formatXTick,
  formatYTick,
  reversed,
  yScaleFactor = 1,
}: AreaChartProps<X>) => {
  const maxValue = data.reduce((acc, current) => (current.value > acc ? current.value : acc), 0)

  const chartData = useMemo(() => (reversed ? data.reverse() : data), [data, reversed])

  return (
    <ResponsiveContainer width="100%" height={292}>
      <RCAreaChart
        className={styles.chart}
        data={chartData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <XAxis
          className={styles.xAxis}
          dataKey="key"
          interval={0}
          tick={({ index, payload, x, y }: RenderProps<string>) =>
            index === 0 || index === chartData.length - 1 ? (
              <Tick
                dy={'0.71em'}
                textAnchor={index === 0 ? 'start' : 'end'}
                value={formatXTick(payload.value as X)}
                x={x}
                y={y + 5}
              />
            ) : (
              <></>
            )
          }
          tickLine={false}
        />
        <YAxis
          className={styles.yAxis}
          domain={[0, yScaleFactor * maxValue]}
          width={30}
          stroke={'transparent'}
          tick={({ index, payload, x, y }: RenderProps<number>) =>
            index === 0 ? (
              <Tick
                dy={'0.355em'}
                textAnchor={'end'}
                value={formatYTick(payload.value)}
                x={x}
                y={y}
              />
            ) : (
              <></>
            )
          }
          tickLine={true}
        />
        <RCTooltip
          cursor={{
            stroke: '#7D7D7D',
            strokeWidth: 1,
            strokeDasharray: '2 2',
          }}
          labelClassName={styles.label}
          wrapperClassName={styles.tooltip}
          itemStyle={{ background: 'red' }}
          content={({ active, label, payload }: TooltipProps<number, number>) =>
            active && label ? (
              <Tooltip
                formatValue={formatTooltipValue ?? formatYTick}
                label={formatTooltipLabel(label as string)}
                values={payload?.map((value) => value.value!) ?? []}
              />
            ) : null
          }
        />
        <defs>
          <linearGradient
            id="colorValue"
            x1="405"
            y1="10.5"
            x2="405"
            y2="191"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#9DD2B0" />
            <stop offset="1" stopColor="#9DD2B0" stopOpacity="0" />
          </linearGradient>
        </defs>
        <Area
          activeDot={{ fill: 'white', r: 5, stroke: '#9DD2B0', strokeWidth: 3 }}
          dataKey="value"
          fill="url(#colorValue)"
          fillOpacity={1}
          stroke="#9DD2B0"
          strokeLinejoin="round"
          strokeWidth={2.4}
          type="linear"
        />
      </RCAreaChart>
    </ResponsiveContainer>
  )
}

export default AreaChart
